import { Check, ChevronDown, ChevronRight, Copy, ExternalLink, Search } from 'lucide-react';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { Input } from '../../../components/ui/input.js';
import { cn } from '../../../lib/utils.js';
import type { CustomFieldsItem, PlanningObjectItem, WorkfrontObjectItem } from '../hooks.js';
import type { ScenarioRow } from '../types.js';
import { isActive } from '../types.js';

/**
 * License-wide nested view shown when the operator picks
 * "Workfront Objects (All)" from the scenarios-page view dropdown.
 * Mirrors the legacy `wf-audit/src/slices/scenario/components/
 * scenario/WorkfrontObjectsTable.tsx`: each scenario is its own
 * expandable block; inside the block, `Workfront IDs (N)` +
 * `Custom Fields (N)` + `Planning Objects (N)` render as nested
 * expandable groups. Every leaf row carries the full column shape
 * (ID + search/copy buttons | Name | Object Type | Module Name |
 * Module #) so the operator never has to drill in and out for the
 * thing they're looking for.
 *
 * Per-row Search button (when `onSearch` is wired) calls back into
 * the page-level search input — same affordance as the per-row
 * expand-panel on the Default view. Operators can pivot from "I
 * see this ID here" to "show me every scenario that references it"
 * with one click.
 *
 * Scenarios start collapsed (the page can hold 100+ rows; defaulting
 * to open would render thousands of nested table rows on first
 * paint). Groups default open *inside* an expanded scenario — that
 * matches the legacy and keeps the click count low for the common
 * "open one scenario, scan everything" flow.
 */

const WORKFRONT_IDS = 'Workfront IDs';
const CUSTOM_FIELDS = 'Custom Fields';
const PLANNING_OBJECTS = 'Planning Objects';

// Maestro `planningObjectType` is one of four single-letter codes:
// `Ws` (Workspace), `Rt` (Record Type), `F` (Field), `Rc` (Record).
const PLANNING_TYPE_LABEL: Record<string, string> = {
  Ws: 'Workspace',
  Rt: 'Record Type',
  F: 'Field',
  Rc: 'Record',
};

interface CustomFieldEntry {
  customField: string;
  modules: ReadonlyArray<{
    fusionModuleId: number | string;
    fusionModuleName: string;
    fusionModuleType?: string;
  }>;
}

export function WorkfrontObjectsAllTable({
  filteredScenarios,
  workfrontObjectsByScenario,
  customFieldsByScenario,
  planningObjectsByScenario,
  onSearch,
}: {
  filteredScenarios: ReadonlyArray<ScenarioRow>;
  workfrontObjectsByScenario: Map<string, ReadonlyArray<WorkfrontObjectItem>>;
  customFieldsByScenario: Map<string, CustomFieldsItem>;
  planningObjectsByScenario: Map<string, ReadonlyArray<PlanningObjectItem>>;
  onSearch?: (query: string) => void;
}): JSX.Element {
  const [q, setQ] = useState('');
  // Per-scenario open state. Scenarios start collapsed — see header
  // comment.
  const [openScenarios, setOpenScenarios] = useState<Set<string>>(() => new Set());
  // Per-group closed state, keyed by `${fusionId}|${groupTitle}`. We
  // store the *closed* set (vs the open set) so groups default open
  // inside an expanded scenario without us having to seed state every
  // time the operator expands a new scenario.
  const [closedGroups, setClosedGroups] = useState<Set<string>>(() => new Set());

  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const handleCopy = useCallback((value: string | number): void => {
    const str = String(value);
    void navigator.clipboard?.writeText(str);
    setCopiedKey(str);
    setTimeout(() => {
      setCopiedKey((prev) => (prev === str ? null : prev));
    }, 2000);
  }, []);

  const toggleScenario = useCallback((id: string): void => {
    setOpenScenarios((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const toggleGroup = useCallback((key: string): void => {
    setClosedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  /**
   * Scenarios with at least one detected child object. Hides
   * scenarios that have nothing to show in any of the three groups
   * — matches the legacy view, where empty scenarios are filtered
   * out entirely rather than rendered as empty expandable blocks.
   */
  const populatedScenarios = useMemo(() => {
    return filteredScenarios.filter((s) => {
      const wf = workfrontObjectsByScenario.get(s.fusionId) ?? [];
      if (wf.length > 0) return true;
      const pl = planningObjectsByScenario.get(s.fusionId) ?? [];
      if (pl.length > 0) return true;
      const cf = customFieldsByScenario.get(s.fusionId);
      const entries = extractCustomFields(cf);
      return entries.length > 0;
    });
  }, [
    filteredScenarios,
    workfrontObjectsByScenario,
    customFieldsByScenario,
    planningObjectsByScenario,
  ]);

  /**
   * Filter scenarios by the in-view search input. A scenario is a
   * match if its name matches, OR any of its child objects match —
   * same semantics as the page-level deep search but scoped to the
   * scenarios we're already showing in this view.
   */
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return populatedScenarios;
    return populatedScenarios.filter((s) => {
      if (s.name?.toLowerCase().includes(needle)) return true;
      const wf = workfrontObjectsByScenario.get(s.fusionId) ?? [];
      for (const item of wf) {
        const hay = [
          item.workfrontId,
          item.workfrontName,
          item.workfrontTypeLabel,
          item.workfrontType,
          item.fusionModuleName,
          item.fusionModuleType,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (hay.includes(needle)) return true;
      }
      const cfEntries = extractCustomFields(customFieldsByScenario.get(s.fusionId));
      for (const entry of cfEntries) {
        if (entry.customField.toLowerCase().includes(needle)) return true;
        for (const m of entry.modules) {
          if (m.fusionModuleName.toLowerCase().includes(needle)) return true;
        }
      }
      const pl = planningObjectsByScenario.get(s.fusionId) ?? [];
      for (const item of pl) {
        const hay = [
          item.planningObjectId,
          item.objectName,
          item.recordTypeName,
          item.fusionModuleName,
          item.fusionModuleType,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (hay.includes(needle)) return true;
      }
      return false;
    });
  }, [
    q,
    populatedScenarios,
    workfrontObjectsByScenario,
    customFieldsByScenario,
    planningObjectsByScenario,
  ]);

  return (
    <div className="rounded-md border border-foreground/10 bg-foreground/[0.02]">
      <div className="border-b border-foreground/10 px-3 py-2">
        <Input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search across scenario, ID, name, module…"
          className="max-w-md border-foreground/15 bg-foreground/5 text-sm text-foreground placeholder:text-foreground/40"
        />
      </div>
      {filtered.length === 0 ? (
        <div className="px-4 py-12 text-center text-sm text-foreground/40">
          {populatedScenarios.length === 0
            ? 'No Workfront objects, custom fields, or planning objects detected for the visible scenarios.'
            : `No matches for “${q}”.`}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-foreground/10 text-xs uppercase tracking-wide text-foreground/50">
              <tr>
                <th className="w-8 p-2" />
                <th className="px-3 py-2 font-medium">Scenario / ID / Custom Field</th>
                <th className="px-3 py-2 font-medium">Name</th>
                <th className="px-3 py-2 font-medium">Object Type</th>
                <th className="px-3 py-2 font-medium">Module Name</th>
                <th className="px-3 py-2 text-right font-medium">Module #</th>
              </tr>
            </thead>
            <tbody className="text-foreground/85">
              {filtered.map((scenario) => (
                <ScenarioBlock
                  key={scenario.fusionId}
                  scenario={scenario}
                  workfrontObjects={workfrontObjectsByScenario.get(scenario.fusionId) ?? []}
                  customFields={customFieldsByScenario.get(scenario.fusionId)}
                  planningObjects={planningObjectsByScenario.get(scenario.fusionId) ?? []}
                  expanded={openScenarios.has(scenario.fusionId)}
                  onToggle={() => toggleScenario(scenario.fusionId)}
                  closedGroups={closedGroups}
                  onToggleGroup={toggleGroup}
                  onSearch={onSearch}
                  onCopy={handleCopy}
                  copiedKey={copiedKey}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="border-t border-foreground/10 px-3 py-2 text-xs text-foreground/40">
        {filtered.length === populatedScenarios.length
          ? `${filtered.length} scenario${filtered.length === 1 ? '' : 's'}`
          : `${filtered.length} of ${populatedScenarios.length} scenarios`}
      </div>
    </div>
  );
}

function ScenarioBlock({
  scenario,
  workfrontObjects,
  customFields,
  planningObjects,
  expanded,
  onToggle,
  closedGroups,
  onToggleGroup,
  onSearch,
  onCopy,
  copiedKey,
}: {
  scenario: ScenarioRow;
  workfrontObjects: ReadonlyArray<WorkfrontObjectItem>;
  customFields: CustomFieldsItem | undefined;
  planningObjects: ReadonlyArray<PlanningObjectItem>;
  expanded: boolean;
  onToggle: () => void;
  closedGroups: Set<string>;
  onToggleGroup: (key: string) => void;
  onSearch: ((q: string) => void) | undefined;
  onCopy: (v: string | number) => void;
  copiedKey: string | null;
}): JSX.Element {
  const cfEntries = useMemo(() => extractCustomFields(customFields), [customFields]);
  const wfKey = `${scenario.fusionId}|${WORKFRONT_IDS}`;
  const cfKey = `${scenario.fusionId}|${CUSTOM_FIELDS}`;
  const plKey = `${scenario.fusionId}|${PLANNING_OBJECTS}`;
  const wfOpen = !closedGroups.has(wfKey);
  const cfOpen = !closedGroups.has(cfKey);
  const plOpen = !closedGroups.has(plKey);
  const active = isActive(scenario);

  return (
    <Fragment>
      <tr
        onClick={onToggle}
        className="cursor-pointer border-b border-foreground/10 bg-foreground/[0.02] hover:bg-foreground/[0.05]"
      >
        <td className="p-2 align-middle">
          <span className="text-foreground/50">
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </span>
        </td>
        <td colSpan={5} className="px-3 py-2.5">
          <div className="flex items-center gap-3">
            <span className="font-medium text-foreground">{scenario.name}</span>
            <span
              className={cn(
                'rounded-full px-2 py-0.5 text-xs font-medium',
                active
                  ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                  : 'bg-rose-500/15 text-rose-700 dark:text-rose-300',
              )}
            >
              {active ? 'Active' : 'Inactive'}
            </span>
          </div>
        </td>
      </tr>
      {expanded ? (
        <>
          {workfrontObjects.length > 0 ? (
            <Fragment>
              <GroupHeader
                title={WORKFRONT_IDS}
                count={workfrontObjects.length}
                expanded={wfOpen}
                onToggle={() => onToggleGroup(wfKey)}
              />
              {wfOpen
                ? workfrontObjects.map((item) => (
                    <WorkfrontDataRow
                      key={`wf-${item.id}`}
                      item={item}
                      scenarioUrl={scenario.url}
                      onSearch={onSearch}
                      onCopy={onCopy}
                      copiedKey={copiedKey}
                    />
                  ))
                : null}
            </Fragment>
          ) : null}
          {cfEntries.length > 0 ? (
            <Fragment>
              <GroupHeader
                title={CUSTOM_FIELDS}
                count={cfEntries.length}
                expanded={cfOpen}
                onToggle={() => onToggleGroup(cfKey)}
              />
              {cfOpen
                ? cfEntries.map((entry, i) => (
                    <CustomFieldDataRow
                      key={`cf-${i}-${entry.customField}`}
                      entry={entry}
                      scenarioUrl={scenario.url}
                      onSearch={onSearch}
                      onCopy={onCopy}
                      copiedKey={copiedKey}
                    />
                  ))
                : null}
            </Fragment>
          ) : null}
          {planningObjects.length > 0 ? (
            <Fragment>
              <GroupHeader
                title={PLANNING_OBJECTS}
                count={planningObjects.length}
                expanded={plOpen}
                onToggle={() => onToggleGroup(plKey)}
              />
              {plOpen
                ? planningObjects.map((item, i) => (
                    <PlanningDataRow
                      key={`pl-${scenario.fusionId}-${i}-${item.planningObjectId}-${item.fusionModuleId ?? 'nomod'}`}
                      item={item}
                      scenarioUrl={scenario.url}
                      onSearch={onSearch}
                      onCopy={onCopy}
                      copiedKey={copiedKey}
                    />
                  ))
                : null}
            </Fragment>
          ) : null}
        </>
      ) : null}
    </Fragment>
  );
}

function GroupHeader({
  title,
  count,
  expanded,
  onToggle,
}: {
  title: string;
  count: number;
  expanded: boolean;
  onToggle: () => void;
}): JSX.Element {
  return (
    <tr
      onClick={onToggle}
      className="cursor-pointer border-b border-foreground/10 hover:bg-foreground/[0.04]"
    >
      <td className="p-2 pl-6 align-middle">
        <span className="text-foreground/50">
          {expanded ? (
            <ChevronDown className="h-3.5 w-3.5" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" />
          )}
        </span>
      </td>
      <td colSpan={5} className="px-3 py-1.5">
        <span className="text-xs font-medium uppercase tracking-wider text-foreground/60">
          {title} ({count})
        </span>
      </td>
    </tr>
  );
}

function WorkfrontDataRow({
  item,
  scenarioUrl,
  onSearch,
  onCopy,
  copiedKey,
}: {
  item: WorkfrontObjectItem;
  scenarioUrl: string | null | undefined;
  onSearch: ((q: string) => void) | undefined;
  onCopy: (v: string | number) => void;
  copiedKey: string | null;
}): JSX.Element {
  return (
    <tr className="border-b border-foreground/5 text-sm text-foreground/85">
      <td className="p-2" />
      <td className="px-3 py-2 pl-10 font-mono text-xs">
        <span className="inline-flex items-center gap-1">
          {item.workfrontLink ? (
            <a
              href={item.workfrontLink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-700 dark:text-blue-300 underline hover:text-blue-800 dark:hover:text-blue-200"
            >
              {item.workfrontId}
            </a>
          ) : (
            item.workfrontId
          )}
          <RowActions
            value={item.workfrontId}
            onSearch={onSearch}
            onCopy={onCopy}
            copied={copiedKey === String(item.workfrontId)}
          />
        </span>
      </td>
      <td className="px-3 py-2">{item.workfrontName ?? '—'}</td>
      <td className="px-3 py-2 text-xs text-foreground/60">
        {item.workfrontTypeLabel || item.workfrontType || '—'}
      </td>
      <td className="px-3 py-2">{item.fusionModuleName || '—'}</td>
      <td className="px-3 py-2 text-right">
        {scenarioUrl ? (
          <a
            href={`${scenarioUrl}?moduleId=${item.fusionModuleId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 font-mono text-xs text-blue-700 dark:text-blue-300 underline hover:text-blue-800 dark:hover:text-blue-200"
          >
            {item.fusionModuleId}
            <ExternalLink className="h-3 w-3" />
          </a>
        ) : (
          <span className="font-mono text-xs">{item.fusionModuleId}</span>
        )}
      </td>
    </tr>
  );
}

function CustomFieldDataRow({
  entry,
  scenarioUrl,
  onSearch,
  onCopy,
  copiedKey,
}: {
  entry: CustomFieldEntry;
  scenarioUrl: string | null | undefined;
  onSearch: ((q: string) => void) | undefined;
  onCopy: (v: string | number) => void;
  copiedKey: string | null;
}): JSX.Element {
  // When the field is referenced from exactly one module, link the
  // Module # cell to that module's deep-link inside the Fusion editor.
  // Multiple modules → fall back to a count summary; the per-scenario
  // expand panel's custom-fields section already gives the operator a
  // drill-in route via the search affordance.
  const single = entry.modules.length === 1 ? entry.modules[0] : null;
  return (
    <tr className="border-b border-foreground/5 text-sm text-foreground/85">
      <td className="p-2" />
      <td className="px-3 py-2 pl-10 font-mono text-xs">
        <span className="inline-flex items-center gap-1">
          {entry.customField}
          <RowActions
            value={entry.customField}
            onSearch={onSearch}
            onCopy={onCopy}
            copied={copiedKey === entry.customField}
          />
        </span>
      </td>
      <td className="px-3 py-2">{entry.customField}</td>
      <td className="px-3 py-2 text-xs text-foreground/60">Custom Field</td>
      <td className="px-3 py-2">
        {single ? single.fusionModuleName : `${entry.modules.length} modules`}
      </td>
      <td className="px-3 py-2 text-right font-mono text-xs">
        {single ? (
          scenarioUrl ? (
            <a
              href={`${scenarioUrl}?moduleId=${single.fusionModuleId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-blue-700 dark:text-blue-300 underline hover:text-blue-800 dark:hover:text-blue-200"
            >
              {single.fusionModuleId}
              <ExternalLink className="h-3 w-3" />
            </a>
          ) : (
            String(single.fusionModuleId)
          )
        ) : (
          '—'
        )}
      </td>
    </tr>
  );
}

function PlanningDataRow({
  item,
  scenarioUrl,
  onSearch,
  onCopy,
  copiedKey,
}: {
  item: PlanningObjectItem;
  scenarioUrl: string | null | undefined;
  onSearch: ((q: string) => void) | undefined;
  onCopy: (v: string | number) => void;
  copiedKey: string | null;
}): JSX.Element {
  const name = item.objectName ?? item.recordTypeName ?? '—';
  const typeLabel = PLANNING_TYPE_LABEL[item.planningObjectType] ?? item.planningObjectType;
  return (
    <tr className="border-b border-foreground/5 text-sm text-foreground/85">
      <td className="p-2" />
      <td className="px-3 py-2 pl-10 font-mono text-xs">
        <span className="inline-flex items-center gap-1">
          {item.planningObjectId}
          <RowActions
            value={item.planningObjectId}
            onSearch={onSearch}
            onCopy={onCopy}
            copied={copiedKey === item.planningObjectId}
          />
        </span>
      </td>
      <td className="px-3 py-2">{name}</td>
      <td className="px-3 py-2 text-xs text-foreground/60">{typeLabel}</td>
      <td className="px-3 py-2">{item.fusionModuleName || '—'}</td>
      <td className="px-3 py-2 text-right">
        {item.fusionModuleId ? (
          scenarioUrl ? (
            <a
              href={`${scenarioUrl}?moduleId=${item.fusionModuleId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 font-mono text-xs text-blue-700 dark:text-blue-300 underline hover:text-blue-800 dark:hover:text-blue-200"
            >
              {item.fusionModuleId}
              <ExternalLink className="h-3 w-3" />
            </a>
          ) : (
            <span className="font-mono text-xs">{item.fusionModuleId}</span>
          )
        ) : (
          '—'
        )}
      </td>
    </tr>
  );
}

function RowActions({
  value,
  onSearch,
  onCopy,
  copied,
}: {
  value: string | number;
  onSearch: ((q: string) => void) | undefined;
  onCopy: (v: string | number) => void;
  copied: boolean;
}): JSX.Element {
  return (
    <span className="ml-1 inline-flex items-center gap-0.5">
      {onSearch ? (
        <button
          type="button"
          onClick={(e) => {
            // Clicks on the row-action buttons bubble to the parent
            // row, which is the group-header / scenario-header toggle.
            // Stop propagation so clicking Search doesn't also collapse
            // the surrounding group.
            e.stopPropagation();
            onSearch(String(value));
          }}
          className="rounded p-1 text-foreground/50 hover:bg-foreground/10 hover:text-foreground/80"
          title={`Search scenarios by ${value}`}
          aria-label={`Search scenarios by ${value}`}
        >
          <Search className="h-3 w-3" />
        </button>
      ) : null}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onCopy(value);
        }}
        className="rounded p-1 text-foreground/50 hover:bg-foreground/10 hover:text-foreground/80"
        title={`Copy ${value}`}
        aria-label={copied ? `Copied ${value}` : `Copy ${value}`}
      >
        {copied ? <Check className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3" />}
      </button>
    </span>
  );
}

interface RawCustomFieldEntry {
  customField?: unknown;
  modules?: unknown;
}

/**
 * `customFieldsData` is open-shaped JSONB. The engine populates it
 * with an array of `{ customField, modules: [{ fusionModuleId,
 * fusionModuleName, fusionModuleType }] }` entries — one per detected
 * field. Older rows may have a different shape; only entries that
 * actually look like the documented format are surfaced. Mirrors the
 * matching helper in `ScenarioExpandPanel.tsx`; keeping both
 * inline-copies in lockstep when the engine's emit shape changes.
 */
function extractCustomFields(cf: CustomFieldsItem | undefined): ReadonlyArray<CustomFieldEntry> {
  if (!cf || cf.customFieldsData === null || cf.customFieldsData === undefined) return [];
  const raw = cf.customFieldsData;
  if (!Array.isArray(raw)) return [];
  const out: CustomFieldEntry[] = [];
  for (const item of raw as ReadonlyArray<RawCustomFieldEntry>) {
    if (typeof item.customField !== 'string') continue;
    const modules = Array.isArray(item.modules)
      ? (item.modules as ReadonlyArray<Record<string, unknown>>)
          .map((m) => ({
            fusionModuleId: (m.fusionModuleId ?? '') as number | string,
            fusionModuleName: typeof m.fusionModuleName === 'string' ? m.fusionModuleName : '',
            fusionModuleType:
              typeof m.fusionModuleType === 'string' ? m.fusionModuleType : undefined,
          }))
          .filter((m) => m.fusionModuleId !== '' || m.fusionModuleName)
      : [];
    out.push({ customField: item.customField, modules });
  }
  return out;
}
